import { atom } from 'jotai';
import { MapRef } from 'react-map-gl';
import type { TcMapFilterMode, MapBounds } from '../types';
import type { SurfaceRecord } from './surface';

// Per-surface MAP interaction state.
//
// The inventory page mounts two `TypeCurveMap` instances at once — the Main Page
// map (behind the modal) and the Unit Builder's edit map. If they shared the map
// ref / viewport / filter mode / AOI / shape-edit working ring, one map's pan,
// filter switch, or vertex drag would move the other (the working-ring leak even
// dragged the OTHER map's cross-section line). Each of these becomes a
// `{ main, builder }` record; the component reads `xAtoms[useSurface()]`.
//
// The plain Type Curve pages and every existing main-page consumer keep importing
// the old names (`tcMapRefAtom`, …), which `type-curve-filters/atoms.ts` now
// re-exports as the `.main` entry — so they are untouched.
//
// (Deliberately NOT per-surface: `mapConfigAtom` layer toggles stay shared — the
// two maps show the same base layers, which both already did, and it avoids
// forking the config atom's `onMount` normalize.)
function createSurfaceMapAtoms() {
  const ref = atom<MapRef | null>(null);
  const bounds = atom<MapBounds | null>(null);
  const filterMode = atom<TcMapFilterMode>('type-curve-area');
  const spatialFilterDisabled = atom(false);
  // Effective mode consumed by query hooks: falls back to 'type-curve-area' when
  // the spatial filter is disabled.
  const effectiveFilterMode = atom<TcMapFilterMode>((get) => {
    return get(spatialFilterDisabled) ? 'type-curve-area' : get(filterMode);
  });
  const aoiPolygon = atom<GeoJSON.Polygon | null>(null);
  const aoiPolygonLocked = atom(true);
  // True while the AOI draw tool owns map clicks (MapboxDraw owns the top layers).
  const aoiDrawing = atom(
    (get) =>
      get(effectiveFilterMode) === 'aoi-polygon' &&
      (!get(aoiPolygon) || !get(aoiPolygonLocked))
  );
  const unitShapeEditing = atom(false);
  const unitShapeEditRequested = atom(false);
  const unitShapeWorkingRing = atom<GeoJSON.Position[] | null>(null);
  // True when unit click-to-select must stand down (AOI draw, or shape editor
  // vertex handles over the unit).
  const gesturesSuspended = atom(
    (get) => get(unitShapeEditing) || get(aoiDrawing)
  );
  return {
    ref,
    bounds,
    filterMode,
    spatialFilterDisabled,
    effectiveFilterMode,
    aoiPolygon,
    aoiPolygonLocked,
    aoiDrawing,
    unitShapeEditing,
    unitShapeEditRequested,
    unitShapeWorkingRing,
    gesturesSuspended,
  };
}

const surfaces = {
  main: createSurfaceMapAtoms(),
  builder: createSurfaceMapAtoms(),
};

export const mapRefAtoms: SurfaceRecord<typeof surfaces.main.ref> = {
  main: surfaces.main.ref,
  builder: surfaces.builder.ref,
};
export const mapBoundsAtoms = {
  main: surfaces.main.bounds,
  builder: surfaces.builder.bounds,
};
export const mapFilterModeAtoms = {
  main: surfaces.main.filterMode,
  builder: surfaces.builder.filterMode,
};
export const spatialFilterDisabledAtoms = {
  main: surfaces.main.spatialFilterDisabled,
  builder: surfaces.builder.spatialFilterDisabled,
};
export const effectiveMapFilterModeAtoms = {
  main: surfaces.main.effectiveFilterMode,
  builder: surfaces.builder.effectiveFilterMode,
};
export const aoiPolygonAtoms = {
  main: surfaces.main.aoiPolygon,
  builder: surfaces.builder.aoiPolygon,
};
export const aoiPolygonLockedAtoms = {
  main: surfaces.main.aoiPolygonLocked,
  builder: surfaces.builder.aoiPolygonLocked,
};
export const aoiDrawingAtoms = {
  main: surfaces.main.aoiDrawing,
  builder: surfaces.builder.aoiDrawing,
};
export const unitShapeEditingAtoms = {
  main: surfaces.main.unitShapeEditing,
  builder: surfaces.builder.unitShapeEditing,
};
export const unitShapeEditRequestedAtoms = {
  main: surfaces.main.unitShapeEditRequested,
  builder: surfaces.builder.unitShapeEditRequested,
};
export const unitShapeWorkingRingAtoms = {
  main: surfaces.main.unitShapeWorkingRing,
  builder: surfaces.builder.unitShapeWorkingRing,
};
export const mapGesturesSuspendedAtoms = {
  main: surfaces.main.gesturesSuspended,
  builder: surfaces.builder.gesturesSuspended,
};
